import axios, { AxiosError } from 'axios';

// API configuration - Check runtime config first, then build-time env, then fallback
const API_BASE_URL = (window as any).REACT_APP_API_URL || process.env.REACT_APP_API_URL || 'http://localhost:5002/api';

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor for auth
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      console.log('Adding token to request:', config.url);
    } else {
      console.log('No token found for request:', config.url);
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling - SAFE VERSION
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const url = error.config?.url;
    const status = error.response?.status;
    console.log('API Error:', url, status);
    
    // Only redirect on 401 if it's a critical authentication failure
    if (status === 401) {
      const isLoginRequest = url?.includes('/auth/login');
      const isProfileRequest = url?.includes('/auth/profile');
      const currentPath = window.location.pathname;
      
      // Only logout if it's a login or profile request failure
      // Don't logout for dashboard API failures
      if ((isLoginRequest || isProfileRequest) && currentPath !== '/admin/login') {
        console.log('Critical auth failure - logging out');
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/admin/login';
      } else {
        console.log('API call failed but keeping user logged in');
      }
    }
    
    return Promise.reject(error);
  }
);

// Types
export interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  role: string;
  status: string;
  createdAt: string;
  membershipType?: string;
  membershipStatus?: string;
}

export interface Benefit {
  id: string;
  name: string;
  description: string;
  category: string;
  discount: string;
  partner: string;
  plans: string[];
  usageLimit: number;
  active: boolean;
}

export interface Agent {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  agentCode: string;
  signups?: number;
  commission?: number;
  status: string;
  createdAt: string;
}

export interface Game {
  id: string;
  name: string;
  type: string;
  entryFee: number;
  prizePool: number;
  startDate: string;
  endDate: string;
  participants: number;
  status: 'upcoming' | 'live' | 'completed';
}

export interface DashboardStats {
  totalUsers: number;
  activeMembers: number;
  totalRevenue: number;
  monthlyGrowth: number;
  activeGiveaways: number;
  totalRedemptions: number;
  newSignups: number;
  activeAgents: number;
}

// Auth services
export const authService = {
  login: async (credentials: { email: string; password: string }) => {
    const response = await api.post('/auth/login', credentials);
    if (response.data.token) {
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
    }
    return response.data;
  },

  logout: async () => {
    try {
      await api.post('/auth/logout');
    } finally {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/admin/login';
    }
  },

  getProfile: async () => {
    const response = await api.get('/auth/profile');
    return response.data;
  },
};

// Admin Dashboard services
export const dashboardService = {
  getStats: async (): Promise<DashboardStats> => {
    const response = await api.get('/admin/dashboard/stats');
    return response.data;
  },

  getRecentActivity: async () => {
    const response = await api.get('/admin/dashboard/activity');
    return response.data;
  },

  getChartData: async (type: string) => {
    const response = await api.get(`/admin/dashboard/charts/${type}`);
    return response.data;
  },
};

// User management services
export const userService = {
  getAll: async (params?: { page?: number; limit?: number; search?: string }) => {
    const response = await api.get('/admin/users', { params });
    return response.data;
  },

  getById: async (id: string): Promise<User> => {
    const response = await api.get(`/admin/users/${id}`);
    return response.data;
  },

  create: async (data: Partial<User>) => {
    const response = await api.post('/admin/users', data);
    return response.data;
  },

  update: async (id: string, data: Partial<User>) => {
    const response = await api.put(`/admin/users/${id}`, data);
    return response.data;
  },

  delete: async (id: string) => {
    const response = await api.delete(`/admin/users/${id}`);
    return response.data;
  },

  suspend: async (id: string) => {
    const response = await api.post(`/admin/users/${id}/suspend`);
    return response.data;
  },

  activate: async (id: string) => {
    const response = await api.post(`/admin/users/${id}/activate`);
    return response.data;
  },
};

// Benefits management services
export const benefitsService = {
  getAll: async (): Promise<Benefit[]> => {
    const response = await api.get('/admin/benefits');
    return response.data;
  },

  getById: async (id: string): Promise<Benefit> => {
    const response = await api.get(`/admin/benefits/${id}`);
    return response.data;
  },

  create: async (data: Partial<Benefit>) => {
    const response = await api.post('/admin/benefits', data);
    return response.data;
  },

  update: async (id: string, data: Partial<Benefit>) => {
    const response = await api.put(`/admin/benefits/${id}`, data);
    return response.data;
  },

  delete: async (id: string) => {
    const response = await api.delete(`/admin/benefits/${id}`);
    return response.data;
  },

  toggleStatus: async (id: string) => {
    const response = await api.post(`/admin/benefits/${id}/toggle`);
    return response.data;
  },
};

// Agent management services
export const agentService = {
  getAll: async (): Promise<Agent[]> => {
    const response = await api.get('/admin/agents');
    return response.data;
  },

  getById: async (id: string): Promise<Agent> => {
    const response = await api.get(`/admin/agents/${id}`);
    return response.data;
  },

  create: async (data: Partial<Agent>) => {
    const response = await api.post('/admin/agents', data);
    return response.data;
  },

  update: async (id: string, data: Partial<Agent>) => {
    const response = await api.put(`/admin/agents/${id}`, data);
    return response.data;
  },

  delete: async (id: string) => {
    const response = await api.delete(`/admin/agents/${id}`);
    return response.data;
  },

  getPerformance: async (id: string) => {
    const response = await api.get(`/admin/agents/${id}/performance`);
    return response.data;
  },

  getLeaderboard: async () => {
    const response = await api.get('/admin/agents/leaderboard');
    return response.data;
  },
};

// Giveaways management services
export const giveawaysService = {
  getAll: async (): Promise<any[]> => {
    const response = await api.get('/admin/giveaways');
    return response.data;
  },

  getById: async (id: string): Promise<any> => {
    const response = await api.get(`/admin/giveaways/${id}`);
    return response.data;
  },

  create: async (data: any) => {
    const response = await api.post('/admin/giveaways', data);
    return response.data;
  },

  update: async (id: string, data: any) => {
    const response = await api.put(`/admin/giveaways/${id}`, data);
    return response.data;
  },

  delete: async (id: string) => {
    const response = await api.delete(`/admin/giveaways/${id}`);
    return response.data;
  },
};

// Games management services
export const gamesService = {
  getAll: async (): Promise<Game[]> => {
    const response = await api.get('/admin/games');
    return response.data;
  },

  getById: async (id: string): Promise<Game> => {
    const response = await api.get(`/admin/games/${id}`);
    return response.data;
  },

  create: async (data: Partial<Game>) => {
    const response = await api.post('/admin/games', data);
    return response.data;
  },

  update: async (id: string, data: Partial<Game>) => {
    const response = await api.put(`/admin/games/${id}`, data);
    return response.data;
  },

  delete: async (id: string) => {
    const response = await api.delete(`/admin/games/${id}`);
    return response.data;
  },

  start: async (id: string) => {
    const response = await api.post(`/admin/games/${id}/start`);
    return response.data;
  },

  end: async (id: string) => {
    const response = await api.post(`/admin/games/${id}/end`);
    return response.data;
  },

  drawWinners: async (id: string) => {
    const response = await api.post(`/admin/games/${id}/draw`);
    return response.data;
  },
};

// Reports services
export const reportsService = {
  getRevenue: async (period: string) => {
    const response = await api.get(`/admin/reports/revenue?period=${period}`);
    return response.data;
  },

  getMembership: async () => {
    const response = await api.get('/admin/reports/membership');
    return response.data;
  },

  getRedemptions: async () => {
    const response = await api.get('/admin/reports/redemptions');
    return response.data;
  },

  getAgentPerformance: async () => {
    const response = await api.get('/admin/reports/agent-performance');
    return response.data;
  },

  generateReport: async (type: string, params: any) => {
    const response = await api.post('/admin/reports/generate', { type, ...params });
    return response.data;
  },

  exportReport: async (type: string, format: 'csv' | 'pdf' = 'csv') => {
    const response = await api.get(`/admin/reports/export/${type}?format=${format}`, {
      responseType: 'blob',
    });
    return response.data;
  },
};

// Notification services
export const notificationService = {
  send: async (data: { 
    recipients: string[]; 
    title: string; 
    message: string; 
    type: string;
  }) => {
    const response = await api.post('/admin/notifications/send', data);
    return response.data;
  },

  getTemplates: async () => {
    const response = await api.get('/admin/notifications/templates');
    return response.data;
  },

  createTemplate: async (data: any) => {
    const response = await api.post('/admin/notifications/templates', data);
    return response.data;
  },
};

// Settings services
export const settingsService = {
  getAll: async () => {
    const response = await api.get('/admin/settings');
    return response.data;
  },

  update: async (key: string, value: any) => {
    const response = await api.put('/admin/settings', { key, value });
    return response.data;
  },

  getSystemInfo: async () => {
    const response = await api.get('/admin/settings/system');
    return response.data;
  },
};

// Token management services
export const tokenService = {
  getUsersWithTokens: async (params?: { page?: number; limit?: number; search?: string }) => {
    const response = await api.get('/admin/users/tokens', { params });
    return response.data;
  },

  updateUserTokens: async (userId: number, amount: number, reason: string) => {
    const response = await api.post(`/admin/users/${userId}/tokens`, { amount, reason });
    return response.data;
  },

  getTokenHistory: async (limit = 50, offset = 0, userId?: number) => {
    const params: any = { limit, offset };
    if (userId) {
      params.userId = userId;
    }
    const response = await api.get('/admin/tokens/history', { params });
    return response.data;
  },

  getTokenStats: async () => {
    const response = await api.get('/admin/tokens/stats');
    return response.data;
  },
};

// Subscription token services
export const subscriptionTokenService = {
  getTokenSettings: async () => {
    const response = await api.get('/admin/subscriptions/token-settings');
    return response.data;
  },

  updateTokenSettings: async (settings: any[]) => {
    const response = await api.put('/admin/subscriptions/token-settings', { settings });
    return response.data;
  },

  allocateMonthlyTokens: async () => {
    const response = await api.post('/admin/subscriptions/allocate-monthly-tokens');
    return response.data;
  },

  getAllocationHistory: async () => {
    const response = await api.get('/admin/subscriptions/allocation-history');
    return response.data;
  },

  allocateUserTokens: async (userId: string) => {
    const response = await api.post('/admin/subscriptions/allocate-user-tokens', { userId });
    return response.data;
  },
};

// Plans management services
export const plansService = {
  getAll: async () => {
    const response = await api.get('/admin/plans');
    return response.data;
  },

  getById: async (id: string) => {
    const response = await api.get(`/admin/plans/${id}`);
    return response.data;
  },

  create: async (data: any) => {
    const response = await api.post('/admin/plans', data);
    return response.data;
  },

  update: async (id: string, data: any) => {
    const response = await api.put(`/admin/plans/${id}`, data);
    return response.data;
  },

  delete: async (id: string) => {
    const response = await api.delete(`/admin/plans/${id}`);
    return response.data;
  },
};


// Export the axios instance for custom requests
export default api;